import type { ComponentProps } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@jbnl/shared-ui";

/** No size of its own: a skeleton earns its keep only when the answer landing moves nothing. */
export function Skeleton({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="skeleton"
      aria-hidden="true"
      className={cn("animate-pulse rounded-md bg-accent", className)}
      {...props}
    />
  );
}

/** A bar inside a line box of the real height: a line of text is taller than its glyphs. */
export function SkeletonLine({
  className,
  size = "sm",
}: {
  /** The bar's width, as a Tailwind class. */
  className?: string;
  /** The type size of the line it stands in for. */
  size?: "base" | "sm" | "xs";
}) {
  const [box, bar] = lineSizes[size];

  return (
    <span className={cn("flex items-center", box)}>
      <Skeleton className={cn(bar, "w-full", className)} />
    </span>
  );
}

/** The line box each type size occupies, and the bar that sits inside it. */
const lineSizes = {
  base: ["h-6", "h-4"],
  sm: ["h-5", "h-3.5"],
  xs: ["h-4", "h-2.5"],
} as const;

/**
 * Without this the page falls silent for a screen reader where it used to say "Bezig met laden…".
 * `status` and not `alert`: it is present at render and there is nothing to act on.
 */
export function SkeletonRegion({
  label,
  className,
  children,
}: {
  /** What a screen reader hears while the bars are up. Defaults to the catalog's wording. */
  label?: string;
  className?: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <div role="status" aria-busy="true" className={className}>
      <span className="sr-only">{label ?? t("table.busy")}</span>
      {children}
    </div>
  );
}
